import clsx from 'clsx';
import { useState } from 'react';

import { ExploreChallenges } from './ExploreChallenges';
import {
  GRAVIMETRIC_CHALLENGES,
  GRAVIMETRIC_QUESTION,
  type GravimetricChallengeInput,
  type GravimetricExploreResult,
  type GravimetricSetup,
  type WeighingCount,
  calculateGravimetricExplore,
  defaultGravimetricSetup,
  gravimetricExploreFields,
} from './instrumentalExplore';
import { CONSTANT_MASS_TOLERANCE_G, type GravimetricProblem, formatMass } from './instrumentalGravimetric';
import { ModelInputPanel, WorkedStepsPanel } from './ModelInputs';

const WIDTH = 320;
const HEIGHT = 170;
const LEFT = 56;
const BOTTOM = 26;
const TOP = 14;

/** The learner's weighings as bars above the empty crucible, with the constant-mass band on the last one. */
function ExploreWeighingChart({ crucibleG, result }: { crucibleG: number; result: GravimetricExploreResult }) {
  const { weighings } = result;
  // Bars start just under the lightest weighing so the falling water excess is visible.
  const floor = crucibleG + (Math.min(...weighings) - crucibleG) * 0.9;
  const top = Math.max(...weighings) + (Math.max(...weighings) - floor) * 0.15;
  const plotH = HEIGHT - TOP - BOTTOM;
  const slot = (WIDTH - LEFT - 12) / 3;
  const y = (mass: number) => TOP + plotH - ((mass - floor) / (top - floor)) * plotH;
  const last = weighings[weighings.length - 1];
  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      className="block h-auto w-full max-w-lg"
      role="img"
      aria-label={`Your weighings of crucible plus solid: ${weighings.map((mass) => `${formatMass(mass)} g`).join(', ')}; ${result.constant ? 'constant mass reached' : 'not yet constant'}`}
    >
      <line x1={LEFT} x2={WIDTH - 12} y1={TOP + plotH} y2={TOP + plotH} className="stroke-ink" strokeWidth={1.5} />
      <text x={LEFT - 4} y={TOP + plotH} textAnchor="end" className="fill-slate2 text-[8px] font-bold">
        {floor.toFixed(3)}
      </text>
      <text x={LEFT - 4} y={TOP + 6} textAnchor="end" className="fill-slate2 text-[8px] font-bold">
        {top.toFixed(3)}
      </text>
      <rect
        x={LEFT}
        y={y(last + CONSTANT_MASS_TOLERANCE_G)}
        width={WIDTH - 12 - LEFT}
        height={Math.max(1, y(last - CONSTANT_MASS_TOLERANCE_G) - y(last + CONSTANT_MASS_TOLERANCE_G))}
        className={result.constant ? 'fill-wash-mint' : 'fill-wash-coral'}
      />
      {weighings.map((mass, index) => (
        <g key={index}>
          <rect x={LEFT + slot * index + slot * 0.2} y={y(mass)} width={slot * 0.6} height={TOP + plotH - y(mass)} rx={3} className="fill-brand-sky/70" />
          <text x={LEFT + slot * index + slot / 2} y={TOP + plotH + 12} textAnchor="middle" className="fill-slate2 text-[8px] font-bold">
            #{index + 1}
          </text>
        </g>
      ))}
    </svg>
  );
}

/** Gravimetric Explore: the learner's own masses, the constant-mass check and the full working. */
export function InstrumentalGravimetricExplore({ problem }: { problem: GravimetricProblem }) {
  const [count, setCount] = useState<WeighingCount>(3);
  const [setup, setSetup] = useState<GravimetricSetup>(() => defaultGravimetricSetup(problem));
  const [last, setLast] = useState<GravimetricChallengeInput>(() => ({
    problem,
    setup: defaultGravimetricSetup(problem),
    count: 3,
    result: calculateGravimetricExplore(problem, defaultGravimetricSetup(problem), 3),
  }));
  const [error, setError] = useState<string | null>(null);
  const [panelKey, setPanelKey] = useState(0);

  const apply = (next: GravimetricSetup, nextCount: WeighingCount) => {
    setSetup(next);
    setCount(nextCount);
    try {
      setLast({ problem, setup: next, count: nextCount, result: calculateGravimetricExplore(problem, next, nextCount) });
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'This setup cannot be modelled.');
    }
  };

  return (
    <div className="mt-5 grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      <div className="min-w-0 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-full bg-surface p-1" aria-label="Number of weighings">
            {([2, 3] as WeighingCount[]).map((option) => (
              <button
                key={option}
                type="button"
                aria-pressed={count === option}
                onClick={() => apply(setup, option)}
                className={clsx('rounded-full px-3 py-1.5 text-xs font-extrabold', count === option ? 'bg-ink text-white' : 'text-slate2')}
              >
                {option} weighings
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setPanelKey((key) => key + 1);
              apply(defaultGravimetricSetup(problem), count);
            }}
            className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky"
          >
            Use this problem&apos;s masses
          </button>
        </div>
        <ModelInputPanel
          key={`${count}-${panelKey}`}
          legend="Your balance readings"
          fields={gravimetricExploreFields(count)}
          values={setup}
          onChange={(next) => apply(next, count)}
        />
        {error ? (
          <p role="alert" className="rounded-2xl border border-brand-coral/40 bg-wash-coral px-3 py-2 text-sm font-bold text-ink">
            {error} The model keeps the last valid result.
          </p>
        ) : null}
        <ExploreChallenges question={GRAVIMETRIC_QUESTION} challenges={GRAVIMETRIC_CHALLENGES} result={last} />
      </div>
      <div className="min-w-0 space-y-4">
        <ExploreWeighingChart crucibleG={last.setup.crucibleG} result={last.result} />
        <WorkedStepsPanel
          title="How the model works it out"
          steps={last.result.steps}
          stepTestIdPrefix="gravimetric-step"
          result={last.result.result}
          resultTestId="gravimetric-result"
          limitNote={last.result.limitNote}
        />
      </div>
    </div>
  );
}
